/**
 * Google Identity Services sign-in. The browser receives a Google ID token and posts it to
 * the server, which verifies it and starts a session. The token itself is never stored.
 * When the server runs with DEV_LOGIN, a name + shared password form is shown instead (testing only).
 */
import { useEffect, useState, type FormEvent } from 'react';
import { GoogleLogin, GoogleOAuthProvider } from '@react-oauth/google';
import { t, type Me } from '@kadice/shared';
import { api } from '../api';

export function Login({ onSignedIn }: { onSignedIn: (me: Me) => void }) {
  const [cfg, setCfg] = useState<{ googleClientId: string; devLogin: boolean } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');

  useEffect(() => {
    api.config().then(setCfg, () => setError(t('app.unreachable')));
  }, []);

  const signIn = async (credential: string | undefined) => {
    if (!credential) return setError(t('login.googleFailed'));
    try {
      onSignedIn(await api.signIn(credential));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('login.failed'));
    }
  };

  const devSignIn = async (e: FormEvent) => {
    e.preventDefault();
    try {
      onSignedIn(await api.devSignIn(name, password));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('login.failed'));
    }
  };

  return (
    <main className="login">
      <img src="/img/logo.png" alt={t('app.title')} className="logo" />
      {cfg?.devLogin && (
        <form className="inline" onSubmit={(e) => void devSignIn(e)}>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('login.name')} autoFocus />
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t('login.password')}
            autoComplete="current-password"
          />
          <button type="submit" className="primary" disabled={!name.trim() || !password}>
            {t('login.play')}
          </button>
        </form>
      )}
      {cfg && !cfg.devLogin && (
        <GoogleOAuthProvider clientId={cfg.googleClientId}>
          <GoogleLogin
            onSuccess={(res) => void signIn(res.credential)}
            onError={() => setError(t('login.googleFailed'))}
            theme="filled_black"
          />
        </GoogleOAuthProvider>
      )}
      {error && <p className="error">{error}</p>}
    </main>
  );
}
